<template>
  <div
    :id="block.hash"
    ref="reservation"
  >
    <div
      v-if="!grid && preBuffer"
      class="bg-pattern-lines w-full flex-shrink-0 -z-10"
      :style="{
        height: ((preBuffer || 0) * minuteSize) + 'px'
      }"
    />

    <o-panel-link
      class="relative block w-full overflow-hidden transition duration-300 ease-in-out cursor-pointer border dark:border-gray-800 border-l-0 rounded-md z-10"
      :style="{
        backgroundColor: backgroundColour,
        height: grid ? 'auto' : duration * minuteSize + 'px'
      }"
      :path="`/appointment/${block.reservation.idReservation}?date=${block.reservation.date}`"
      :class="[ disabled ? 'pointer-events-none' : '']"
    >
      <div
        class="h-full absolute w-1 top-0 left-0"
        :style="{ backgroundColor: borderColour }"
      />

      <div class="flex flex-col h-full justify-between p-1 pl-2 text-xs">
        <div class="flex items-start">
          <!--<o-icon v-if="isGroup" icon="reservationClass" :size="16" class=" mr-2 mt-1" />-->
          <div class="w-full">
            <template v-if="isShort && isActive">
              <p
                class="font-medium "
                :class="{ 'line-clamp-1' : duration < 25 }"
              >
                <span>{{ $date.minutes(block.startTime) }} - {{ $date.minutes(block.finishTime) }}</span>
              </p>
              <p
                v-if="!privacyMode"
                class="reservation-block-price font-medium text-gray-800"
                :class="{ 'line-clamp-1' : duration < 25 }"
              >
                <span>{{ block.reservationService.priceLabel }}</span>
              </p>
            </template>

            <template v-else>
              <div class="flex items-center space-x-2 justify-between w-full">
                <div>
                  <p
                    class="font-semibold reservation-block-label"
                    :class="{ 'line-clamp-1' : duration < 25 }"
                  >
                    <span>{{ block.label }}</span>
                    <span v-if="block.isTimingFinishing" class="opacity-75">
                      {{ $t('components.schedule.blocks.reservation.finishing') }}
                    </span>
                  </p>

                  <div class="flex justify-between items-center">
                    <p class=" text-xs">
                      {{ $date.minutes(block.startTime) }} - {{ $date.minutes(block.finishTime) }} <br>
                    </p>
                  </div>

                  <div v-if="!privacyMode && showPrices" class="flex justify-between items-center reservation-block-price">
                    <p class="text-gray-700 text-xs dark:text-gray-200">
                      {{ $t('components.schedule.blocks.reservation.price', { label: block.reservationService.priceLabel }) }}<br>
                    </p>
                  </div>
                </div>
                <template v-if="!privacyMode && customer && customer.isBirthdayToday">
                  <o-tooltip :text="$t('general.birthday', { name: customer.displayName })" class="reservation-block-birthday">
                    <span class="text-lg">🎉</span>
                  </o-tooltip>
                </template>
              </div>

              <p v-if="hasNotes" class=" text-xs reservation-block-notes">
                {{ block.reservation.notes }}
              </p>
            </template>
          </div>
        </div>

        <div v-if="!privacyMode" class="absolute bottom-1 right-1 w-full flex justify-end space-x-1 flex-wrap reservation-block-status">
          <!--<o-chip v-if="hasNotes" size="2xs" variant="info" class="m-px">
            Notes
          </o-chip>-->
          <o-chip v-if="status" size="2xs" :colour="status.colour" class="m-px whitespace-nowrap">
            {{ status.label }}
          </o-chip>
          <o-chip v-if="isCustomerConfirmed" size="2xs" variant="success" class="m-px">
            {{ $t('reservation.attending') }}
          </o-chip>
          <o-chip v-if="wasBookedOnline" size="2xs" variant="info" class="m-px">
            {{ $t('general.online') }}
          </o-chip>
          <o-chip v-if="pass" size="2xs" variant="success" class="m-px">
            {{ $t('general.pass') }}
          </o-chip>
          <o-chip v-if="isResourceBlock" size="2xs" variant="info" class="m-px">
            {{ $t('general.resource') }}
          </o-chip>
          <o-chip v-if="isCheckedIn" size="2xs" variant="info" class="m-px">
            {{ $t('schedule.checkedIn') }}
          </o-chip>
          <o-chip v-if="isUnconfirmed" size="2xs" variant="warning" class="m-px">
            {{ $t('reservation.unconfirmed') }}
          </o-chip>
          <o-chip v-if="isNoShow" size="2xs" variant="warning" class="m-px">
            {{ $t('reservation.noShow') }}
          </o-chip>
          <o-chip v-if="isPaid && !pass" size="2xs" variant="success" class="m-px">
            {{ $t('general.paid') }}
          </o-chip>
          <o-chip v-if="hasBouncedEmail" size="2xs" variant="warning" class="m-px">
            {{ $t('general.bounced') }}
          </o-chip>
          <o-chip v-if="isFull" size="2xs" variant="danger" class="m-px">
            {{ $t('general.full') }}
          </o-chip>
          <o-chip v-if="isCourse" size="2xs" variant="info" class="m-px">
            Course
          </o-chip>

          <customer-alerts v-if="customer" :customer="customer">
            <o-chip size="2xs" variant="danger" class="m-px">
              <o-icon icon="warning" :size="10" />
            </o-chip>
          </customer-alerts>
        </div>
      </div>
    </o-panel-link>

    <div
      v-if="!grid && postBuffer"
      class="bg-pattern-lines w-full flex-shrink-0 -z-10"
      :style="{
        height: ((postBuffer || 0) * minuteSize) + 'px'
      }"
    />
  </div>
</template>

<script setup lang="ts">
import Color from 'color'

import CustomerAlerts from '@/components/customer/elements/Alerts.vue'

import { useUIStore } from '~/stores/useUIStore'

const { $auth, $colorMode } = useNuxtApp()
const uiStore = useUIStore()

const modifier = inject('modifier', null) as any
const container = inject('container', null) as any

interface Props {
  block: any
  grid?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  grid: false,
})

const reservation = ref<any>()
const hovered = ref(false)
const disabled = ref(false)

const privacyMode = computed(() => uiStore.privacyMode)

const backgroundColour = computed(() => {
  if ($colorMode.value === 'dark') {
    const colour = Color(props.block.colour.border)
    return colour.darken(0.6).hex()
  }

  return props.block.colour.background
})

const borderColour = computed(() => {
  return props.block.colour.border
})

const scrolling = computed(() => container?.scrolling || false)
const customer = computed(() => props.block.reservation.customer)
const duration = computed(() => props.block.duration)

const bufferTimes = computed(() => {
  return props.block.bufferTimes || props.block.relatedBlocks
    ?.find((block: any) => block.bufferTimes && block.reservationService.idReservationService === props.block.reservationService.idReservationService)
    ?.bufferTimes
})

const preBuffer = computed(() => bufferTimes.value ? bufferTimes.value.preBuffer : 0)
const postBuffer = computed(() => bufferTimes.value ? bufferTimes.value.postBuffer : 0)

const pass = computed(() => {
  return props.block.reservationService.customerPass && props.block.reservationService.customerPass.pass ? props.block.reservationService.customerPass : null
})

const status = computed(() => {
  const s = props.block.reservation.statusType

  if (s && s.shortName) {
    return {
      label: s.shortName,
      colour: s.hexColour,
    }
  }

  return null
})

const minuteSize = computed(() => {
  const value = Number($auth?.info?.minuteSize)
  return Number.isFinite(value) && value > 0 ? value : 2
})
const showPrices = computed(() => $auth.info.location?.scheduleShowPrices)
const isShort = computed(() => duration.value * minuteSize.value < 40)
const isActive = computed(() => modifier?.active.move === props.block)
const isGroup = computed(() => props.block.reservation.isGroup)
const isCheckedIn = computed(() => props.block.reservation.checkedIn)
const isPaid = computed(() => props.block.reservation.isPaid)
const isUnconfirmed = computed(() => props.block.reservation.isStatusUnconfirmed)
const isNoShow = computed(() => props.block.reservation.noShow)
const isResourceBlock = computed(() => props.block.isResourceBlock)
const isCustomerConfirmed = computed(() => props.block.reservation.customerConfirmed)

const isFull = computed(() => {
  const res = props.block.reservation

  if (res.isGroup) {
    return (res.attendees?.count || 0) >= res.customerMax
  }

  return false
})

const isCourse = computed(() => props.block.reservation.courseRunReservations?.length > 0)
const hasBouncedEmail = computed(() => !!(props.block.reservation.customer && props.block.reservation.customer.emailBounced))
const hasNotes = computed(() => !!props.block.reservation.notes)
const wasBookedOnline = computed(() => props.block.reservation.wasBookedOnline)

watch(() => props.block, () => {
  console.log('change')
}, { deep: true })

watch(scrolling, (val) => {
  if (!val) {
    reservation.value?.addEventListener('mouseenter', handleMouseEnter)
    reservation.value?.addEventListener('mouseleave', handleMouseLeave)
  } else {
    reservation.value?.removeEventListener('mouseenter', handleMouseEnter)
    reservation.value?.removeEventListener('mouseleave', handleMouseLeave)
  }

  setTimeout(() => {
    disabled.value = val
  }, 200)
})

onMounted(() => {
  reservation.value?.addEventListener('mouseenter', handleMouseEnter)
  reservation.value?.addEventListener('mouseleave', handleMouseLeave)
})

function handleMouseEnter() {
  if (modifier) {
    modifier.active.hover = props.block
  }
}

function handleMouseLeave() {
  if (modifier) {
    hovered.value = false
    modifier.active.hover = null
  }
}
</script>
